import React, { useEffect, useState } from 'react'
import { StatisticsLinesChart } from '../../../components/Statistics'
import { parse } from 'papaparse'

const DATES_RANGE = {
  FIRST_30_DAYS: 'first_30_days',
  LAST_WEEK: 'last_week',
  CUSTOM: 'custom'
}

const MESSAGE_TYPES = {
  ALL: 'total',
  RBM: 'RCS',
  SMS: 'SMS'
}

const MMESSAGE_TYPES_MAP = {
  [MESSAGE_TYPES.RBM]: 'RBM',
  [MESSAGE_TYPES.SMS]: 'SMS',
  [MESSAGE_TYPES.ALL]: 'Total'
}

const STATUS_MAP = {
  delivered: 'Delivered',
  sent: 'Sent',
  displayed: 'Displayed (RBM)',
  clicks: 'Clicks (RBM)'
}

const getDataForChart = (dateStats, messagesType, datesRange) => {
  if (!dateStats || !dateStats[messagesType] || typeof dateStats[messagesType] !== 'object') {
    return []
  }
  
  if (!datesRange || !Array.isArray(datesRange) || datesRange.length < 2) {
    return []
  }
  
  const initDate = new Date(datesRange[0])
  const endDate = new Date(datesRange[1])
  
  if (isNaN(initDate.getTime()) || isNaN(endDate.getTime())) {
    return []
  }
  
  const STATUS = ['delivered', 'sent']
  const TEMPLATE_DAY = {
    date: '',
    delivered: 0,
    sent: 0
  }
  if (messagesType !== MESSAGE_TYPES.SMS) {
    STATUS.push('displayed')
    TEMPLATE_DAY.displayed = 0
    STATUS.push('clicks')
    TEMPLATE_DAY.clicks = 0
  }
  const data = []
  const currentDate = new Date(initDate)
  
  while (currentDate <= endDate) {
    const date = currentDate.toLocaleDateString('en-GB')
    const dayStats = dateStats[messagesType][date] || TEMPLATE_DAY
    for (let messageStatus of STATUS) {
      data.push({
        date,
        category: STATUS_MAP[messageStatus],
        value: dayStats[messageStatus] || 0
      })
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return data
}

const MessgaeTypeButton = ({ type, selectedType, onClick }) => {
  const label = MMESSAGE_TYPES_MAP[type]
  
  // Don't render if no label
  if (!label) {
    return null
  }
  
  const isSelected = selectedType === type
  
  return (
    <button
      className={`min-w-[80px] border-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-lg px-5 py-2.5 font-semibold text-sm transition-all duration-200 ${
        isSelected
          ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-200/50'
          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-indigo-400 hover:text-indigo-700 shadow-sm'
      }`}
      onClick={() => onClick(type)}
    >
      {label}
    </button>
  )
}

const calculateEndDate = (initDate, days) => {
  const endDate = new Date(initDate)
  endDate.setDate(initDate.getDate() + days)
  return endDate
}

const parseStringToDate = (date) => {
  if (!date || typeof date !== 'string') {
    return new Date() // Return current date as fallback
  }
  const dateSplited = date.split('/')
  if (dateSplited.length !== 3) {
    return new Date() // Return current date as fallback
  }
  return new Date(dateSplited[2], dateSplited[1] - 1, dateSplited[0])
}

const parseDateToInputString = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    const today = new Date()
    let month = today.getMonth() + 1
    if (month < 10) {
      month = `0${month}`
    }
    let day = today.getDate()
    if (day < 10) {
      day = `0${day}`
    }
    return `${today.getFullYear()}-${month}-${day}`
  }
  
  let month = date.getMonth() + 1
  if (month < 10) {
    month = `0${month}`
  }

  let day = date.getDate()
  if (day < 10) {
    day = `0${day}`
  }

  return `${date.getFullYear()}-${month}-${day}`
}

const getFirstDateFromStats = (dateStats) => {
  if (!dateStats || typeof dateStats !== 'object') {
    return new Date() // Return current date as fallback
  }
  
  const dates = Object.keys(dateStats).filter(date => date && typeof date === 'string')
  if (dates.length === 0) {
    return new Date() // Return current date as fallback
  }
  
  dates.sort((a, b) => {
    try {
      return parseStringToDate(a) - parseStringToDate(b)
    } catch (e) {
      return 0
    }
  })
  
  try {
    return parseStringToDate(dates[0])
  } catch (e) {
    return new Date() // Return current date as fallback
  }
}

export const CampaignLinesChart = ({ dateStats, campaignTimestamp, dateRange }) => {
  // En vistas agregadas (global / template) el rango ya lo fija el filtro de
  // arriba y llega como `dateRange` {from,to} ISO. En ese caso NO mostramos el
  // selector propio (era un segundo control confuso) y el gráfico usa esas
  // mismas fechas. En vista de campaña individual (sin dateRange) se mantiene
  // el comportamiento de siempre.
  const useExternalRange = !!(dateRange && dateRange.from && dateRange.to)
  const [datesRangeType, setDatesRangeType] = useState(
    DATES_RANGE.FIRST_30_DAYS
  )
  const [messagesType, setMessagesType] = useState(MESSAGE_TYPES.ALL)
  const [datesRange, setDatesRange] = useState([new Date(), new Date()])
  
  // Calculate default dates based on campaign timestamp
  const getDefaultDates = () => {
    if (campaignTimestamp) {
      try {
        const launchDate = new Date(campaignTimestamp)
        if (!isNaN(launchDate.getTime())) {
          const endDate = new Date(launchDate)
          endDate.setDate(endDate.getDate() + 30)
          return { launchDate, endDate }
        }
      } catch (e) {
        console.warn('Invalid campaign timestamp:', campaignTimestamp)
      }
    }
    // Fallback to first date from stats
    const statsData = dateStats?.[MESSAGE_TYPES.ALL]
    if (statsData && typeof statsData === 'object' && Object.keys(statsData).length > 0) {
      const firstDate = getFirstDateFromStats(statsData)
      return { launchDate: firstDate, endDate: calculateEndDate(firstDate, 30) }
    }
    // Final fallback: use current date
    const today = new Date()
    const endDate = new Date(today)
    endDate.setDate(endDate.getDate() + 30)
    return { launchDate: today, endDate }
  }

  const defaultDates = getDefaultDates()
  const [customInitDate, setCustomInitDate] = useState(
    parseDateToInputString(defaultDates.launchDate)
  )
  const [customEndDate, setCustomEndDate] = useState(
    parseDateToInputString(defaultDates.endDate)
  )
  const [chartData, setChartData] = useState([])

  useEffect(() => {
    if (!dateStats) return
    if (useExternalRange) {
      // Usar exactamente el rango del filtro de arriba (global/template).
      const from = new Date(dateRange.from)
      const to = new Date(dateRange.to)
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) {
        const range = [from, to]
        setDatesRange(range)
        setChartData(getDataForChart(dateStats, messagesType, range))
        return
      }
    }
    if (dateStats[MESSAGE_TYPES.ALL]) {
      handleDateRangeTypeChange(DATES_RANGE.FIRST_30_DAYS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignTimestamp, dateStats, useExternalRange, dateRange && dateRange.from, dateRange && dateRange.to, messagesType])

  const handleDateRangeTypeChange = (type) => {
    let newDateRange = []
    try {
      switch (type) {
        case DATES_RANGE.FIRST_30_DAYS:
          // Use campaign timestamp if available, otherwise use first date from stats
          if (campaignTimestamp) {
            try {
              const launchDate = new Date(campaignTimestamp)
              if (!isNaN(launchDate.getTime())) {
                newDateRange = [launchDate, calculateEndDate(launchDate, 30)]
                break
              }
            } catch (e) {
              console.warn('Invalid campaign timestamp:', campaignTimestamp)
            }
          }
          // Fallback to first date from stats
          const statsData = dateStats?.[MESSAGE_TYPES.ALL]
          if (statsData && typeof statsData === 'object' && Object.keys(statsData).length > 0) {
            const firstDate = getFirstDateFromStats(statsData)
            newDateRange = [firstDate, calculateEndDate(firstDate, 30)]
          } else {
            // Final fallback: use current date
            const today = new Date()
            newDateRange = [today, calculateEndDate(today, 30)]
          }
          break
        case DATES_RANGE.LAST_WEEK:
          newDateRange = [calculateEndDate(new Date(), -7), new Date()]
          break
        case DATES_RANGE.CUSTOM:
          const initDate = new Date(customInitDate)
          const endDate = new Date(customEndDate)
          if (!isNaN(initDate.getTime()) && !isNaN(endDate.getTime())) {
            newDateRange = [initDate, endDate]
          } else {
            const today = new Date()
            newDateRange = [today, calculateEndDate(today, 30)]
          }
          break
        default:
          const today = new Date()
          newDateRange = [today, calculateEndDate(today, 30)]
          break
      }
    } catch (e) {
      console.error('Error setting date range:', e)
      const today = new Date()
      newDateRange = [today, calculateEndDate(today, 30)]
    }
    
    setDatesRange(newDateRange)
    if (dateStats && dateStats[messagesType] && typeof dateStats[messagesType] === 'object') {
      try {
        setChartData(getDataForChart(dateStats, messagesType, newDateRange))
      } catch (e) {
        console.error('Error generating chart data:', e)
        setChartData([])
      }
    } else {
      setChartData([])
    }
  }

  const handleApplyCustomRange = () => {
    const initDate = new Date(customInitDate)
    const endDate = new Date(customEndDate)
    if (initDate > endDate) {
      alert('End date must be greater than start date')
      return
    }
    if (endDate > new Date()) {
      alert('End date must be before or equal to toaday')
      return
    }
    if (initDate < new Date('2019-01-01')) {
      alert('Start date must be greater than 01/01/2019')
      return
    }
    setDatesRange([initDate, endDate])
    setChartData(getDataForChart(dateStats, messagesType, [initDate, endDate]))
  }

  const handleChangeCustomRange = (date, isInit) => {
    if (isInit) {
      setCustomInitDate(date)
    } else {
      setCustomEndDate(date)
    }
  }

  const handleMessageTypeChange = (type) => {
    setMessagesType(type)
    setChartData(getDataForChart(dateStats, type, datesRange))
  }

  return (
    <section id="messageStats" className="flex flex-col gap-6">
      <div id="filters" className="flex flex-col gap-4">
        <div id="datesFilter" className="flex flex-col gap-2">
          <label className="block text-sm font-semibold text-grey-blue mb-2">
            Date Range
          </label>
          {useExternalRange ? (
            <div className="text-sm text-gray-500">
              Rango del filtro:{' '}
              <span className="font-medium text-grey-blue">
                {String(dateRange.from).slice(0, 10)}
              </span>{' '}
              →{' '}
              <span className="font-medium text-grey-blue">
                {String(dateRange.to).slice(0, 10)}
              </span>
            </div>
          ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <select
              onChange={(e) => {
                handleDateRangeTypeChange(e.target.value)
                setDatesRangeType(e.target.value)
              }}
              value={datesRangeType}
              className="border-2 border-indigo-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 rounded-lg px-4 py-2 text-sm font-medium text-grey-blue bg-white shadow-sm transition-all"
            >
              <option value={DATES_RANGE.FIRST_30_DAYS}>First 30 days</option>
              <option value={DATES_RANGE.LAST_WEEK}>Last week</option>
              <option value={DATES_RANGE.CUSTOM}>Custom range</option>
            </select>
            {datesRangeType === DATES_RANGE.CUSTOM && (
              <>
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-grey-blue whitespace-nowrap">
                    Start date:
                  </label>
                  <input
                    value={customInitDate}
                    onChange={(e) => {
                      handleChangeCustomRange(e.target.value, true)
                    }}
                    type="date"
                    className="border-2 border-indigo-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="block text-sm font-medium text-grey-blue whitespace-nowrap">
                    End date:
                  </label>
                  <input
                    value={customEndDate}
                    onChange={(e) =>
                      handleChangeCustomRange(e.target.value, false)
                    }
                    type="date"
                    className="border-2 border-indigo-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm"
                  />
                </div>
                <button
                  className="px-5 py-2 rounded-lg bg-blue-dark text-white font-semibold text-sm hover:bg-indigo-700 transition-colors shadow-sm hover:shadow-md"
                  onClick={handleApplyCustomRange}
                >
                  Apply
                </button>
              </>
            )}
          </div>
          )}
        </div>
        <div id="messagesTypeFilter" className="flex flex-col gap-2">
          <label className="block text-sm font-semibold text-grey-blue mb-2">
            Message Type
          </label>
          <div className="flex gap-3 flex-wrap">
            {Object.values(MESSAGE_TYPES)
              .filter((type) => {
                // Skip RBM if there's no RBM data
                if (type === MESSAGE_TYPES.RBM && (!dateStats || !dateStats[MESSAGE_TYPES.RBM])) {
                  return false
                }
                // Only show types that have a label in the map
                return MMESSAGE_TYPES_MAP[type] !== undefined
              })
              .map((type) => (
                <MessgaeTypeButton
                  key={type}
                  type={type}
                  selectedType={messagesType}
                  onClick={handleMessageTypeChange}
                />
              ))}
          </div>
        </div>
      </div>
      <div id="charts groups" className="flex gap-4">
        <div className="w-full h-[250px]">
          {chartData && chartData.length > 0 ? (
            <StatisticsLinesChart data={chartData} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg
                className="w-12 h-12 mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-sm font-medium">No hay datos disponibles para este período</p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
